import type { Lang } from "../index";

export interface SalesEngineerDiscoveryCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: SalesEngineerDiscoveryCopy = {
  seo: {
    title: "Minuta for sales engineers — live English call copilot",
    description:
      "When a prospect asks the hard technical, pricing, or compliance question in English, Minuta suggests the answer live from your docs, battlecards, and call context.",
  },
  eyebrow: "Use case · Sales engineer",
  h1: "The hard question lands. Your English answer is already forming.",
  lede:
    "Minuta is the live copilot for sales engineers who need to respond well in difficult English calls. Load the docs before the meeting; when pressure hits, Minuta turns the transcript and your context into a clean answer you can say out loud.",
  problem: {
    heading: "Notes after the call are too late for the pressure moment",
    body1:
      "You're presenting in English. The prospect — a CTO with 20 years of opinions on the topic — asks, \"How would this handle 200k users if our IdP starts throttling logins during rollout?\" You know the architecture. You even have the SSO runbook. But the precise English answer is hard to assemble while everyone is waiting.",
    body2:
      "A transcript and summary tomorrow can help the CRM. They cannot rescue the four seconds when trust is being won or lost. Minuta stays beside the live call so you can answer now, not only follow up later.",
  },
  scenarios: {
    heading: "The moments Minuta is built for",
    items: [
      {
        tag: "Technical objection",
        title: "\"What happens if our SSO provider rate-limits logins?\"",
        body: "Before the call, you load the SSO runbook and implementation notes. Minuta hears the live question, combines those docs with the rollout constraints just mentioned, and suggests: \"The login path fails closed, queues retry attempts, and keeps admins on email fallback while we coordinate IdP limits.\" You answer with specifics instead of asking to follow up.",
      },
      {
        tag: "Pricing pushback",
        title: "\"Why are you 30% more than [competitor]?\"",
        body: "You attached the competitor battlecard and the prospect's stated pain: support hours lost to manual review. Minuta drafts an English response that ties price to avoided escalations, zero-retention processing, and rollout effort. The call moves from discount pressure back to business impact.",
      },
      {
        tag: "Compliance",
        title: "\"Can legal approve this for HIPAA-regulated tenants?\"",
        body: "You uploaded the security one-pager, DPA notes, and approved language before the meeting. Minuta surfaces the safe wording and drafts a concise answer about secure audio handling and review steps. You stay accurate without inventing a policy on the call.",
      },
    ],
  },
  how: {
    heading: "How Minuta plugs into the next discovery call",
    body:
      "Open Minuta a minute before the Zoom/Teams/Meet call. Attach the proposal, battlecards, technical docs, security one-pagers, and any notes from the account team. Then run the call as you always would.",
    bullets: [
      "No bot joins. The prospect sees nothing — Minuta runs on your computer, captures system audio.",
      "Live English transcript on the side, with the prospect's exact wording and the context that came before it.",
      "Hard question detected → 1-click \"What should I say?\" → grounded English draft in 2-4 seconds, in your tone.",
      "After the call, you still get structured notes. The difference is that the important answer already happened during the call.",
    ],
  },
  proof: {
    heading: "Why sales engineers use Minuta as a copilot, not just a notetaker",
    bullets: [
      "Post-call notes are useful for memory. Minuta is for the live moment when the prospect is waiting for your answer.",
      "Your docs, battlecards, and call context ground the suggestion. The draft stays tied to material you approved before the meeting.",
      "Bot-in-the-call is a non-starter for many enterprise prospects. Minuta is invisible.",
      "Zero-retention security. The audio of a regulated-industry discovery call is processed via secure transient APIs and stays on your computer.",
    ],
  },
  cta: {
    heading: "Bring the hard question to your next customer call",
    body: "Load your docs, join your next discovery or customer call, and see whether Minuta helps you answer in the moment. 7 days unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: SalesEngineerDiscoveryCopy = {
  seo: {
    title: "Minuta para sales engineer — copiloto ao vivo em inglês",
    description:
      "Quando o prospect faz a pergunta técnica, de preço ou compliance em inglês, o Minuta sugere a resposta ao vivo a partir dos seus docs, battlecards e contexto da call.",
  },
  eyebrow: "Caso de uso · Sales engineer",
  h1: "Cai a pergunta difícil. Sua resposta em inglês já começa a aparecer.",
  lede:
    "O Minuta é o copiloto ao vivo para sales engineers que precisam responder bem em calls difíceis em inglês. Carregue os docs antes da reunião; quando a pressão chegar, o Minuta transforma transcript e contexto numa resposta limpa para você falar em voz alta.",
  problem: {
    heading: "Notas depois da call chegam tarde demais para o momento de pressão",
    body1:
      "Você está apresentando em inglês. O prospect — CTO com 20 anos de opinião sobre o tema — pergunta: \"Como isso lidaria com 200 mil usuários se nosso IdP começasse a limitar logins durante o rollout?\" Você conhece a arquitetura. Tem até o runbook de SSO. Mas a resposta precisa em inglês é difícil de montar enquanto todo mundo espera.",
    body2:
      "Transcript e resumo amanhã ajudam o CRM. Eles não salvam os quatro segundos em que a confiança está sendo ganha ou perdida. O Minuta fica ao lado da call ao vivo para você responder agora, não só fazer follow-up depois.",
  },
  scenarios: {
    heading: "Os momentos para os quais o Minuta foi feito",
    items: [
      {
        tag: "Objeção técnica",
        title: "\"O que acontece se nosso provedor de SSO limitar logins?\"",
        body: "Antes da call, você carrega o runbook de SSO e as notas de implementação. O Minuta ouve a pergunta ao vivo, combina esses docs com as restrições de rollout recém-mencionadas e sugere: \"The login path fails closed, queues retry attempts, and keeps admins on email fallback while we coordinate IdP limits.\" Você responde com detalhe em vez de pedir para voltar depois.",
      },
      {
        tag: "Pressão em preço",
        title: "\"Por que vocês são 30% mais caros que o [concorrente]?\"",
        body: "Você anexou o battlecard do concorrente e a dor declarada do prospect: horas de suporte perdidas com revisão manual. O Minuta monta uma resposta em inglês que liga preço a menos escalations, processamento com retenção zero e esforço de rollout. A call sai da pressão por desconto e volta para impacto de negócio.",
      },
      {
        tag: "Compliance",
        title: "\"O jurídico aprova isso para clientes regulados por HIPAA?\"",
        body: "Você subiu o one-pager de segurança, notas de DPA e linguagem aprovada antes da reunião. O Minuta mostra a formulação segura e sugere uma resposta concisa sobre processamento seguro de áudio e etapas de review. Você fica preciso sem inventar política na call.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta entra na próxima discovery call",
    body:
      "Abra o Minuta um minuto antes da call no Zoom/Teams/Meet. Anexe proposta, battlecards, docs técnicos, one-pagers de segurança e notas do account team. Depois rode a call como sempre.",
    bullets: [
      "Nenhum bot entra. O prospect não vê nada — o Minuta roda no seu computador e captura o áudio do sistema.",
      "Transcript em inglês ao vivo do lado, com as palavras exatas do prospect e o contexto que veio antes.",
      "Pergunta difícil detectada → 1 clique em \"O que responder\" → rascunho em inglês fundamentado em 2-4 segundos, no seu tom.",
      "Depois da call, você ainda recebe notas estruturadas. A diferença é que a resposta importante já aconteceu durante a call.",
    ],
  },
  proof: {
    heading: "Por que sales engineer usa o Minuta como copiloto, não só como notetaker",
    bullets: [
      "Notas pós-call são úteis para memória. O Minuta serve para o momento ao vivo em que o prospect está esperando a sua resposta.",
      "Seus docs, battlecards e contexto da call fundamentam a sugestão. O rascunho fica ligado ao material que você aprovou antes da reunião.",
      "Bot na call é não-pra muitos prospects enterprise. O Minuta é invisível.",
      "Segurança com retenção zero. Áudio de discovery em setor regulado é processado por APIs transientes seguras e fica salvo no seu computador.",
    ],
  },
  cta: {
    heading: "Leve a pergunta difícil para a próxima call com cliente",
    body: "Carregue seus docs, entre na próxima discovery ou customer call e veja se o Minuta ajuda você a responder no momento. 7 dias desbloqueados. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: SalesEngineerDiscoveryCopy = {
  seo: {
    title: "Minuta para sales engineers — copiloto en vivo en inglés",
    description:
      "Cuando el prospect hace la pregunta técnica, de precio o compliance en inglés, Minuta sugiere la respuesta en vivo desde tus docs, battlecards y contexto de la call.",
  },
  eyebrow: "Caso de uso · Sales engineer",
  h1: "Llega la pregunta difícil. Tu respuesta en inglés ya empieza a formarse.",
  lede:
    "Minuta es el copiloto en vivo para sales engineers que necesitan responder bien en calls difíciles en inglés. Carga los docs antes de la reunión; cuando llega la presión, Minuta convierte el transcript y tu contexto en una respuesta limpia que puedes decir en voz alta.",
  problem: {
    heading: "Las notas después de la call llegan tarde para el momento de presión",
    body1:
      "Estás presentando en inglés. El prospect — un CTO con 20 años de opinión sobre el tema — pregunta: \"¿Cómo manejaría esto 200k usuarios si nuestro IdP empieza a limitar logins durante el rollout?\" Conoces la arquitectura. Incluso tienes el runbook de SSO. Pero la respuesta precisa en inglés cuesta armarla mientras todos esperan.",
    body2:
      "Un transcript y un resumen mañana ayudan al CRM. No rescatan los cuatro segundos en los que la confianza se gana o se pierde. Minuta se queda al lado de la call en vivo para que puedas responder ahora, no solo hacer follow-up después.",
  },
  scenarios: {
    heading: "Los momentos para los que Minuta fue creado",
    items: [
      {
        tag: "Objeción técnica",
        title: "\"¿Qué pasa si nuestro proveedor de SSO limita los logins?\"",
        body: "Antes de la call, cargas el runbook de SSO y las notas de implementación. Minuta oye la pregunta en vivo, combina esos docs con las restricciones de rollout recién mencionadas y sugiere: \"The login path fails closed, queues retry attempts, and keeps admins on email fallback while we coordinate IdP limits.\" Respondes con detalles en vez de pedir volver luego.",
      },
      {
        tag: "Presión de precio",
        title: "\"¿Por qué son 30% más caros que [competidor]?\"",
        body: "Adjuntaste el battlecard del competidor y el dolor declarado del prospect: horas de soporte perdidas en revisión manual. Minuta arma una respuesta en inglés que conecta precio con menos escalations, procesamiento con retención cero y esfuerzo de rollout. La call sale de la presión por descuento y vuelve al impacto de negocio.",
      },
      {
        tag: "Compliance",
        title: "\"¿Legal puede aprobar esto para clientes regulados por HIPAA?\"",
        body: "Subiste el one-pager de seguridad, notas de DPA y lenguaje aprobado antes de la reunión. Minuta muestra la formulación segura y sugiere una respuesta concisa sobre procesamiento seguro de audio y pasos de review. Te mantienes preciso sin inventar una política en la call.",
      },
    ],
  },
  how: {
    heading: "Cómo entra Minuta en la próxima discovery call",
    body:
      "Abre Minuta un minuto antes de la call en Zoom/Teams/Meet. Adjunta la propuesta, battlecards, docs técnicos, one-pagers de seguridad y notas del account team. Luego corre la call como siempre.",
    bullets: [
      "Ningún bot entra. El prospect no ve nada — Minuta corre en tu computadora y captura el audio del sistema.",
      "Transcript en inglés en vivo al lado, con las palabras exactas del prospect y el contexto que vino antes.",
      "Pregunta difícil detectada → 1 clic en \"Qué responder\" → borrador en inglés fundamentado en 2-4 segundos, en tu tono.",
      "Después de la call, sigues recibiendo notas estructuradas. La diferencia es que la respuesta importante ya ocurrió durante la call.",
    ],
  },
  proof: {
    heading: "Por qué un sales engineer usa Minuta como copiloto, no solo como notetaker",
    bullets: [
      "Las notas post-call son útiles para recordar. Minuta es para el momento en vivo en que el prospect espera tu respuesta.",
      "Tus docs, battlecards y contexto de la call fundamentan la sugerencia. El borrador queda ligado al material que aprobaste antes de la reunión.",
      "Bot en la call es un \"no\" para muchos prospects enterprise. Minuta es invisible.",
      "Seguridad con retención cero. El audio de una discovery en sector regulado es procesado por APIs transitorias seguras y se queda en tu computadora.",
    ],
  },
  cta: {
    heading: "Lleva la pregunta difícil a tu próxima call con cliente",
    body: "Carga tus docs, entra a tu próxima discovery o customer call y mira si Minuta te ayuda a responder en el momento. 7 días desbloqueados. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const salesEngineerDiscoveryCopy: Record<Lang, SalesEngineerDiscoveryCopy> = { en, pt, es };
